import React, { useCallback, useState } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { useSpeechInput } from '../hooks/useSpeechInput';

interface VoiceInputButtonProps {
  onTranscript: (text: string) => void;
  onInterim?: (text: string) => void;
  className?: string;
  showTranscript?: boolean;
}

export const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
  onTranscript,
  onInterim,
  className = '',
  showTranscript = true
}) => {
  const [pendingText, setPendingText] = useState('');

  const handleFinalTranscript = useCallback((text: string) => {
    // Accumulate final transcript chunks
    setPendingText(prev => prev + text);
  }, []);

  const {
    isListening,
    interimTranscript,
    isSupported,
    error,
    start,
    stop,
    getFullTranscript
  } = useSpeechInput({
    onFinalTranscript: handleFinalTranscript,
    onInterimTranscript: onInterim,
    continuous: true
  });

  const handleToggle = useCallback(() => {
    if (isListening) {
      stop();
      // Send accumulated transcript when stopping
      const fullText = getFullTranscript();
      if (fullText.trim()) {
        onTranscript(fullText.trim());
      }
      setPendingText('');
    } else {
      setPendingText('');
      start();
    }
  }, [isListening, start, stop, getFullTranscript, onTranscript]);

  if (!isSupported) {
    return (
      <button
        disabled
        className={`p-2 rounded-lg bg-gray-800/50 text-gray-600 cursor-not-allowed ${className}`}
        title="Speech recognition not supported in this browser"
      >
        <MicOff className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="relative flex items-center gap-2">
      <button
        onClick={handleToggle}
        className={`relative p-2 rounded-lg transition-all duration-200 ${
          isListening
            ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/40'
            : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700 hover:text-white border border-gray-700/50'
        } ${className}`}
        title={isListening ? 'Stop listening (send to terminal)' : 'Start voice input'}
      >
        {isListening ? (
          <>
            <MicOff className="w-4 h-4" />
            <span className="absolute inset-0 rounded-lg border-2 border-cyan-400 animate-ping opacity-50" />
          </>
        ) : (
          <Mic className="w-4 h-4" />
        )}
      </button>

      {/* Live transcript preview */}
      {showTranscript && isListening && (pendingText || interimTranscript) && (
        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50">
          <div className="bg-gray-900/95 border border-cyan-500/30 rounded-lg px-3 py-2 max-w-xs shadow-lg">
            <div className="text-[10px] text-cyan-400 mb-1 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Listening...
            </div>
            <p className="text-xs text-gray-300 whitespace-pre-wrap">
              {pendingText}
              <span className="text-cyan-400/70">{interimTranscript}</span>
              <span className="animate-pulse">|</span>
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2">
          <div className="bg-red-900/80 border border-red-500/30 rounded px-2 py-1 text-[10px] text-red-300">
            {error}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceInputButton;
